import type {
  PortfolioGrowthDataPoint,
  PortfolioGrowthResponse,
  PortfolioGrowthTransferBreakdown,
} from "../services/tradeStatisticsApi";

function finiteNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * UI + query helpers aligned with per-account `analytics` on GET /broker/trade-accounts and
 * GET /trades/portfolio-growth (`growth_basis` / `default_growth_basis`).
 */

export function isTradingPnlOnlyGrowth(
  data?: Pick<PortfolioGrowthResponse, "growth_basis"> | null,
  supportsPortfolioGrowth?: boolean
): boolean {
  return (
    supportsPortfolioGrowth === false ||
    data?.growth_basis === "trading_pnl_only" ||
    data?.growth_basis === "pnl_only"
  );
}

/** Y-axis value for one growth point — never mix equity balance into P&L-only series. */
export function growthPointChartValue(
  point: PortfolioGrowthDataPoint,
  pnlOnly: boolean
): number {
  if (pnlOnly) {
    return Number(point.cumulative_pnl) || 0;
  }
  const pv = point.portfolio_value;
  if (pv != null && Number.isFinite(Number(pv))) {
    return Number(pv);
  }
  return Number(point.cumulative_pnl) || 0;
}

/** Cumulative P&L immediately before the first bucket’s `daily_pnl`. */
export function growthPointBaselineCumulativePnl(point: PortfolioGrowthDataPoint): number {
  return (Number(point.cumulative_pnl) || 0) - (Number(point.daily_pnl) || 0);
}

/** Transfer breakdown immediately before the first bucket’s daily transfer delta. */
export function growthPointBaselineTransferBreakdown(
  point: PortfolioGrowthDataPoint
): PortfolioGrowthTransferBreakdown | undefined {
  const breakdown = point.transfer_breakdown;
  if (!breakdown) return undefined;

  const daily = point.daily_transfer_breakdown;
  if (!daily) return breakdown;

  return {
    cumulative_deposits:
      finiteNumber(breakdown.cumulative_deposits) - finiteNumber(daily.deposits),
    cumulative_withdrawals:
      finiteNumber(breakdown.cumulative_withdrawals) - finiteNumber(daily.withdrawals),
    cumulative_financing:
      finiteNumber(breakdown.cumulative_financing) - finiteNumber(daily.financing),
    cumulative_commissions:
      finiteNumber(breakdown.cumulative_commissions) - finiteNumber(daily.commissions),
    net_transfers: finiteNumber(breakdown.net_transfers) - finiteNumber(daily.net),
  };
}

/** Chart card title: prefer server growth_basis; never label PnL-only curve as "Portfolio Growth". */
export function portfolioGrowthChartTitle(params: {
  /** From portfolio growth API or dashboard summary `portfolioGrowth.growth_basis` */
  growthBasis?: string | null;
  /** From active account `analytics.supports_portfolio_growth` */
  supportsPortfolioGrowth?: boolean;
  /** From active account `analytics.default_growth_basis` when no series loaded yet */
  defaultGrowthBasis?: string | null;
}): string {
  const resolved =
    params.growthBasis ?? params.defaultGrowthBasis ?? null;
  if (isTradingPnlOnlyGrowth({ growth_basis: resolved ?? undefined }, params.supportsPortfolioGrowth)) {
    return "Total P&L Trend";
  }
  return "Portfolio Growth";
}

/**
 * When the broker does not support full portfolio/equity curves, the API rejects
 * `growth_basis=portfolio_value...` — send PnL-only (or omit and let the server default).
 */
export function portfolioGrowthBasisQueryParam(
  supportsPortfolioGrowth: boolean | undefined
): string | undefined {
  if (supportsPortfolioGrowth === false) {
    return "trading_pnl_only";
  }
  return undefined;
}

/**
 * Portfolio Growth chart: show the Views menu (portfolio value / P&L / transfers).
 * Hide for cumulative P&L-only curves — same rules as {@link portfolioGrowthChartTitle}.
 */
export function portfolioGrowthShowsViewSelector(params: {
  growthBasis?: string | null;
  supportsPortfolioGrowth?: boolean;
  defaultGrowthBasis?: string | null;
}): boolean {
  const resolved = params.growthBasis ?? params.defaultGrowthBasis ?? null;
  if (isTradingPnlOnlyGrowth({ growth_basis: resolved ?? undefined }, params.supportsPortfolioGrowth)) {
    return false;
  }
  return true;
}
